// src/pages/shop/ShopDebtors.jsx
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { debtors as debtorsApi } from '../../api';
import BottomNav from '../../components/BottomNav';
import Icon from '../../components/Icon';
import { formatMoney } from '../../data/currencies';
import { useMyCurrency } from '../../hooks/useMyShop';
import { useReloadOnFocus } from '../../hooks/useReloadOnFocus';
import { useShopNav } from './ShopHome';
import { isValidWhatsapp } from '../../utils/whatsapp';


export default function ShopDebtors() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const navItems = useShopNav();
  const currency = useMyCurrency();

  const [items, setItems] = useState([]);
  const [totalDebt, setTotalDebt] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  // Две вкладки: все клиенты и только те, у кого есть долг.
  // Раздел называется «Клиенты», потому что это постоянные покупатели,
  // а долг — лишь одно из их свойств, а не сущность.
  const [tab, setTab] = useState('all');   // 'all' | 'debts'
  const [creating, setCreating] = useState(false);

  const load = useCallback((query) => {
    setLoading(true);
    debtorsApi.list(query)
      .then(r => { setItems(r.items || []); setTotalDebt(r.totalDebt || 0); })
      .catch(() => { setItems([]); setTotalDebt(0); })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const id = setTimeout(() => load(q.trim()), 250);
    return () => clearTimeout(id);
  }, [q, load]);
  useReloadOnFocus(() => load(q.trim()));

  // На вкладке «Долги» — только те, у кого он есть
  const visible = useMemo(
    () => (tab === 'debts' ? items.filter(d => d.debt > 0) : items),
    [items, tab],
  );

  return (
    <div className="page">
      <div className="page-header">
        <button className="back-btn" onClick={() => navigate('/')}>
          <Icon name="chevronLeft" size={18} />
        </button>
        <h1 className="page-title" style={{ flex: 1 }}>{t('clients.title')}</h1>
        <button
          onClick={() => setCreating(true)}
          className="btn btn-primary btn-sm"
        >
          <Icon name="plus" size={14} />
          <span style={{ marginLeft: 4 }}>{t('common.add')}</span>
        </button>
      </div>

      <div style={{ padding: '0 20px' }}>
        {/* Вкладки */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
          {[
            { val: 'all',   label: t('clients.tabAll') },
            { val: 'debts', label: t('clients.tabDebts') },
          ].map(x => (
            <button
              key={x.val}
              onClick={() => setTab(x.val)}
              style={{
                flex: 1,
                padding: '9px 12px',
                borderRadius: 'var(--r-sm)',
                border: '1px solid ' + (tab === x.val ? 'var(--brand-primary)' : 'var(--border)'),
                background: tab === x.val ? 'var(--brand-primary)' : 'var(--bg-elevated)',
                color: tab === x.val ? '#fff' : 'var(--fg-muted)',
                fontSize: 13, fontWeight: 600, cursor: 'pointer',
              }}
            >
              {x.label}
            </button>
          ))}
        </div>

        {/* Общий долг — показываем на вкладке долгов */}
        {tab === 'debts' && totalDebt > 0 && (
          <div className="card" style={{ padding: 16, marginBottom: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 13, color: 'var(--fg-muted)' }}>{t('debtors.totalDebt')}</span>
            <span style={{ fontSize: 20, fontWeight: 800, color: 'var(--danger)', fontVariantNumeric: 'tabular-nums', fontFamily: 'var(--font-display)' }}>
              {formatMoney(totalDebt, currency)}
            </span>
          </div>
        )}

        <input className="input" placeholder={t('debtors.searchPlaceholder')} value={q}
          onChange={(e) => setQ(e.target.value)} style={{ marginBottom: 12 }} />

        {loading && <div className="loader" style={{ padding: 24 }}><div className="spinner" /></div>}

        {!loading && visible.length === 0 && (
          <div className="empty-state">
            <div className="icon"><Icon name="wallet" size={28} stroke={1.5} /></div>
            <p>{t('debtors.empty')}</p>
            <p style={{ fontSize: 12, color: 'var(--fg-faint)', marginTop: 4 }}>{t('debtors.emptyHint')}</p>
          </div>
        )}

        {!loading && visible.length > 0 && (
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            {visible.map((d, i) => (
              <button key={d.id} onClick={() => navigate(`/debtors/${d.id}`)}
                style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', background: 'none', border: 'none', borderTop: i === 0 ? 'none' : '1px solid var(--border-subtle)', cursor: 'pointer', textAlign: 'left' }}>
                <div style={{ width: 38, height: 38, borderRadius: '50%', background: 'var(--bg-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontWeight: 700, color: 'var(--fg-muted)' }}>
                  {d.name.slice(0, 1).toUpperCase()}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: 15, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.name}</p>
                  {d.creditBlocked && <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--danger)' }}>{t('debtors.blacklistTag')}</p>}
                  {d.whatsapp
                    ? <p style={{ fontSize: 12, color: 'var(--fg-faint)' }}>{d.whatsapp}</p>
                    : <p style={{ fontSize: 12, color: 'var(--warning, #c77700)' }}>{t('debtors.noWhatsappTag')}</p>}

                </div>
                <span style={{ fontSize: 15, fontWeight: 700, color: d.debt > 0 ? 'var(--danger)' : 'var(--success)', fontVariantNumeric: 'tabular-nums', fontFamily: 'var(--font-display)' }}>
                  {formatMoney(d.debt, currency)}
                </span>
                <Icon name="chevronRight" size={16} color="var(--fg-faint)" />
              </button>
            ))}
          </div>
        )}

        <div style={{ height: 20 }} />
      </div>

      {creating && (
        <CreateSheet
          onClose={() => setCreating(false)}
          onCreated={(created) => {
            setCreating(false);
            if (created?.id) navigate(`/debtors/${created.id}`);
            else load(q.trim());
          }}
        />
      )}

      <BottomNav items={navItems} />
    </div>
  );
}

// Форма создания нового клиента прямо из модуля (раньше клиент заводился
// только при продаже в долг через кассу). Поля совпадают с формой
// редактирования в карточке: имя, WhatsApp, заметки.
function CreateSheet({ onClose, onCreated }) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const save = async () => {
    if (!name.trim() || !isValidWhatsapp(whatsapp) || busy) return;
    setBusy(true);
    setErr('');
    try {
      const created = await debtorsApi.create({
        name: name.trim(),
        whatsapp: whatsapp.trim(),
        notes: notes.trim(),
      });
      onCreated(created);
    } catch (e) {
      setErr(e?.message || t('common.error'));
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal-sheet">
        <h3 className="modal-title">{t('clients.addNew')}</h3>
        <label style={{ fontSize: 12, color: 'var(--fg-muted)' }}>{t('debtors.name')}</label>
        <input className="input" value={name} onChange={(e) => setName(e.target.value)} autoFocus style={{ marginBottom: 8, marginTop: 4 }} />
        <label style={{ fontSize: 12, color: 'var(--fg-muted)' }}>{t('debtors.whatsappRequired')}</label>
        <input className="input" type="tel" value={whatsapp} onChange={(e) => setWhatsapp(e.target.value)} inputMode="tel" style={{ marginBottom: 8, marginTop: 4 }} />
        <label style={{ fontSize: 12, color: 'var(--fg-muted)' }}>{t('debtors.notes')}</label>
        <textarea className="input" value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} style={{ marginBottom: 12, marginTop: 4, resize: 'vertical' }} />
        {err && <p style={{ fontSize: 13, color: 'var(--danger)', marginBottom: 10 }}>{err}</p>}
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-ghost" onClick={onClose} style={{ flex: 1 }}>{t('common.cancel')}</button>
          <button className="btn btn-primary" disabled={!name.trim() || !isValidWhatsapp(whatsapp) || busy} style={{ flex: 2 }} onClick={save}>{busy ? '…' : t('common.save')}</button>

        </div>
      </div>
    </div>
  );
}
